import React, { useEffect, useState } from 'react'
import {
  MotifModal,
  MotifModalHeader,
  MotifModalBody,
  MotifModalFooter,
  MotifButton,
  MotifProgressLoader,
  MotifTooltip,
  MotifIcon,
} from '@ey-xd/motif-react'
import { useDispatch, useSelector } from 'react-redux'
import Xarrow, { Xwrapper } from 'react-xarrows'
import { IconoirInfoCircle } from '@ey-xd/motif-icon'
import {
  useGetColumnListQuery,
  useSaveColumnMappingMutation,
} from '@/services/hrUpload/hrUploadReducer'
import {
  addHRDocumentMapping,
  removeHRDocumentMapping,
  removeSourceHRDocumentMapping,
  updateHRDocumentMapping,
  updateHRMappedStatus,
} from './HRListSlice'
import './MappingModal.scss'
import MappingItem from './MappingItem'

type Props = {
  isVisible: boolean
  setIsVisible: React.Dispatch<React.SetStateAction<boolean>>
  documentId: string
  onIndexingStarted: (documentId: string, jobId: string) => void
}

export default function MappingModal({ isVisible, setIsVisible, documentId, onIndexingStarted }: Props) {
  const [columnMapping, setColumnMapping] = useState({
    sourceColumnId: '',
    targetColumnId: '',
  })
  const [sourceColumnId, setSourceColumnId] = useState<string[]>([])
  const [isRequiredColumnMapped, setIsRequiredColumnMapped] = useState(false)
  const [saveErrorMessage, setSaveErrorMessage] = useState('')

  const columnMappingState = useSelector((state: any) => state.HRList.mappedItem)
  const isColumnMapped =
    columnMappingState.sourceColumns.length > 0 &&
    columnMappingState.sourceColumns.some((item: any) => item.mappedTargetColumnId !== null)

  const dispatch = useDispatch()
  const { data: columnListData, isLoading } = useGetColumnListQuery(documentId, {
    skip: !documentId,
  })
  const [saveColumnMapping, { isLoading: saveLoading }] = useSaveColumnMappingMutation()

  const resetModalState = () => {
    setSaveErrorMessage('')
  }

  useEffect(() => {
    return () => {
      dispatch(addHRDocumentMapping({ sourceColumns: [], targetColumns: [] }))
    }
  }, [dispatch])

  useEffect(() => {
    if (!isVisible) {
      resetModalState()
    }
  }, [isVisible])

  useEffect(() => {
    if (columnListData) {
      dispatch(addHRDocumentMapping(columnListData))
    }
  }, [columnListData, dispatch])

  useEffect(() => {
    if (columnMapping.sourceColumnId && columnMapping.targetColumnId) {
      setSourceColumnId((prev) => [...prev, columnMapping.sourceColumnId])
      dispatch(updateHRDocumentMapping(columnMapping))
      setColumnMapping({ sourceColumnId: '', targetColumnId: '' })
    }
  }, [columnMapping, dispatch])

  const handleSourceMapping = (sourceId: string) => {
    setColumnMapping({
      ...columnMapping,
      sourceColumnId: sourceId,
    })
  }

  const handleTargetMapping = (targetId: string) => {
    setColumnMapping({
      ...columnMapping,
      targetColumnId: targetId,
    })
  }

  const handleRemoveMapping = (targetId: string) => {
    dispatch(removeHRDocumentMapping({ targetColumnId: targetId }))
    setColumnMapping({ sourceColumnId: '', targetColumnId: '' })
  }

  const handleCancel = () => {
    if (saveLoading) {
      return
    }

    setIsRequiredColumnMapped(false)
    resetModalState()
    setIsVisible(false)
    sourceColumnId.forEach((sourceId) => {
      dispatch(removeSourceHRDocumentMapping({ sourceColumnId: sourceId }))
    })
    setSourceColumnId([])
  }

  const isValidColumnMapping = (columnName: string) => {
    let columnID: string | null = null
    columnMappingState.targetColumns.forEach((item: any) => {
      if (item.columnName.toLowerCase() === columnName.toLowerCase()) {
        columnID = item.id
      }
    })

    if (columnID) {
      return columnMappingState.sourceColumns.some((item: any) => item.mappedTargetColumnId === columnID)
    }

    return false
  }

  const handleColumnMapping = async () => {
    const isValidColumnMappingResult =
      isValidColumnMapping('Job Title') || isValidColumnMapping('Department')

    if (!isValidColumnMappingResult) {
      setIsRequiredColumnMapped(true)
      return
    }

    if (isRequiredColumnMapped) {
      setIsRequiredColumnMapped(false)
    }

    const mappedColumnsPayload = {
      columnMappings: columnMappingState.sourceColumns,
    }

    try {
      setSaveErrorMessage('')
      const response = await saveColumnMapping({
        data: mappedColumnsPayload,
        documentId,
      }).unwrap()

      dispatch(updateHRMappedStatus({ documentId, isMapped: true }))

      const jobId = response?.jobId ?? response?.JobId

      if (typeof response === 'string') {
        setSaveErrorMessage('Column mapping was saved, but the backend is still returning the old response format. Restart the API so MapColumns returns the embedding job id.')
        return
      }

      if (!jobId) {
        setSaveErrorMessage('Column mapping was saved, but the embedding job could not be started. Please close this dialog and try again.')
        return
      }

      onIndexingStarted(documentId, jobId)
      handleCancel()
    } catch (_error) {
      setSaveErrorMessage('Unable to save column mapping or start embeddings. Please close this dialog and try again.')
    }
  }

  return (
    <>
      <MotifModal
        onClose={() => {
          handleCancel()
        }}
        show={isVisible}
        size="xl"
        focusTrapOptions={{
          tabbableOptions: {
            displayCheck: 'none',
          },
        }}
      >
        <MotifModalHeader className="no-border-bottom">
          <div className="motif-h6-default-regular">Mapping HR Data</div>
          <div className="motif-card-subheader">
            <span className="motif-body2-default-regular motif-caption-default-light">
              Please link the source field to the target field. This will help you retain relevant information from your HR list for comparison during
              the Segregation of Duties (SoD) analysis. The relationship can be either many-to-one or one-to-one. It is recommended to ensure that
              Employee Name, Email, and Employee ID are correctly identified.
            </span>
          </div>
        </MotifModalHeader>

        <MotifModalBody className="no-padding-top">
          {isLoading ? (
            <MotifProgressLoader role="alertdialog" show variant="default" tip="Loading..." />
          ) : (
            <Xwrapper>
              <div className="mapping-container">
                <div className="mapping-column source">
                  <div className="motif-body1-default-bold mapping-column-header">
                    Source
                    <MotifTooltip
                      allowClick
                      allowHover
                      hideCloseButton
                      flip
                      forceHideCounter={0}
                      id="tooltip-source"
                      offset={[0, 8]}
                      placement="top"
                      style={{ marginLeft: '10px' }}
                      trigger={<MotifIcon src={IconoirInfoCircle} fill="black" stroke="black" />}
                      usePortal
                      variant="default"
                    >
                      <div className="motif-body2-default-bold">Source</div>
                      <div className="motif-caption-default-light motif-tooltip-custom-width">
                        The source lists out names of columns extracted from the HR list.
                      </div>
                    </MotifTooltip>
                  </div>

                  {columnMappingState.sourceColumns.length > 0 &&
                    columnMappingState.sourceColumns.map((source: any) => (
                      <MappingItem
                        key={source.id}
                        text={source.columnName}
                        itemId={source.id}
                        isMapped={source.mappedTargetColumnId !== null}
                        mappedId={source.mappedTargetColumnId}
                        isSource
                        handleMapping={handleSourceMapping}
                        handleRemoveMapping={() => {}}
                      />
                    ))}
                </div>

                <div className="mapping-column target">
                  <div className="motif-body1-default-bold mapping-column-header">
                    Target
                    <MotifTooltip
                      allowHover
                      hideCloseButton
                      flip
                      forceHideCounter={0}
                      id="tooltip-target"
                      offset={[0, 8]}
                      placement="top"
                      trigger={<MotifIcon src={IconoirInfoCircle} fill="black" stroke="black" />}
                      usePortal
                      variant="default"
                    >
                      <div className="motif-body2-default-bold">Target</div>
                      <div className="motif-caption-default-light motif-tooltip-custom-width">
                        The target is data field which we want to retain from the HR list for our mapping.
                      </div>
                    </MotifTooltip>
                  </div>

                  {columnMappingState.targetColumns.map((item: any) => (
                    <MappingItem
                      key={item.id}
                      text={item.columnName}
                      itemId={item.id}
                      isMapped={item.mappedTargetColumnId !== null}
                      mappedId={item.mappedTargetColumnId}
                      isSource={false}
                      handleMapping={handleTargetMapping}
                      handleRemoveMapping={handleRemoveMapping}
                    />
                  ))}
                </div>

                {columnMappingState.sourceColumns.map(
                  (item: any) =>
                    item.id &&
                    item.mappedTargetColumnId && (
                      <Xarrow
                        key={`${item.id}-${item.mappedTargetColumnId}`}
                        start={item.id}
                        end={item.mappedTargetColumnId}
                        color="#1275C1"
                        curveness={0.5}
                      />
                    ),
                )}
              </div>
            </Xwrapper>
          )}
          {saveErrorMessage && (
            <div className="motif-body2-default-regular" style={{ color: '#B42318', marginTop: '16px' }}>
              {saveErrorMessage}
            </div>
          )}
        </MotifModalBody>

        <MotifModalFooter>
          <div className="motif-row justify-content-end gap-1" style={{ alignItems: 'center' }}>
            <div className="motif-caption-default-light">
              {isRequiredColumnMapped && (
                <span className="motif-body2 motif-error-message">Please ensure that you have mapped Job Title or Department fields.</span>
              )}
            </div>

            <MotifButton onClick={() => handleCancel()} size="medium" type="button" variant="secondary">
              Cancel
            </MotifButton>

            <MotifButton
              onClick={handleColumnMapping}
              size="medium"
              type="button"
              variant="primary"
              loading={saveLoading}
              disabled={saveLoading || !isColumnMapped}
            >
              Confirm
            </MotifButton>
          </div>
        </MotifModalFooter>
      </MotifModal>
    </>
  )
}
